/**
 * Role Service
 * API calls for role and permission management
 */

import apiService from './api.service';

/** apiService returns envelope { success, data }; payload is .data (not .data.data) */
function payload<T>(res: { data?: T }): T {
  return res?.data as T;
}

export interface Role {
  id: string;
  company_id: string;
  name: string;
  code: string;
  description?: string;
  is_system_role: boolean;
  is_active: boolean;
  permissions?: Permission[];
  created_at: string;
  updated_at: string;
}

export interface Permission {
  id: string;
  code: string;
  name: string;
  module: string;
  description?: string;
  is_active: boolean;
}

export interface RoleStatistics {
  permissionCount: number;
  userCount: number;
  moduleCount: number;
}

export interface AuditLog {
  id: string;
  user_email: string;
  action_type: string;
  target_type: string;
  target_name: string;
  changes: any;
  created_at: string;
}

class RoleService {
  /** Backend mounts role routes at /api/v1/roles (not under /auth) */
  private baseUrl = '/roles';

  /**
   * Get all roles
   */
  async getRoles(filters?: {
    company_id?: string;
    is_active?: boolean;
    is_system_role?: boolean;
    search?: string;
  }): Promise<Role[]> {
    const params = new URLSearchParams();
    if (filters?.company_id) params.append('company_id', filters.company_id);
    if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters?.is_system_role !== undefined) params.append('is_system_role', filters.is_system_role.toString());
    if (filters?.search) params.append('search', filters.search);

    const response = await apiService.get<Role[]>(`${this.baseUrl}?${params.toString()}`);
    const list = payload(response);
    return Array.isArray(list) ? list : [];
  }

  /**
   * Get role by ID
   */
  async getRole(id: string): Promise<Role> {
    const response = await apiService.get(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  /**
   * Create new role
   */
  async createRole(data: {
    name: string;
    code: string;
    description?: string;
    /** Omitted for super admin — server resolves default company */
    company_id?: string;
  }): Promise<Role> {
    const response = await apiService.post<Role>(this.baseUrl, data);
    return payload(response);
  }

  /**
   * Update role
   */
  async updateRole(id: string, data: Partial<Role>): Promise<Role> {
    const response = await apiService.put<Role>(`${this.baseUrl}/${id}`, data);
    return payload(response);
  }

  /**
   * Delete role
   */
  async deleteRole(id: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Activate role
   */
  async activateRole(id: string): Promise<Role> {
    const response = await apiService.post<Role>(`${this.baseUrl}/${id}/activate`);
    return payload(response);
  }

  /**
   * Deactivate role
   */
  async deactivateRole(id: string): Promise<Role> {
    const response = await apiService.post(`${this.baseUrl}/${id}/deactivate`);
    return response.data.data;
  }

  /**
   * Clone role
   */
  async cloneRole(
    sourceRoleId: string,
    data: {
      name: string;
      code: string;
      description?: string;
      company_id?: string;
    }
  ): Promise<Role> {
    const response = await apiService.post<Role>(`${this.baseUrl}/${sourceRoleId}/clone`, data);
    return payload(response);
  }

  /**
   * Get all available permissions
   */
  async getAllPermissions(filters?: {
    module?: string;
    is_active?: boolean;
    search?: string;
  }): Promise<Permission[]> {
    const params = new URLSearchParams();
    if (filters?.module) params.append('module', filters.module);
    if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters?.search) params.append('search', filters.search);

    const response = await apiService.get<Permission[]>(
      `${this.baseUrl}/permissions?${params.toString()}`
    );
    const list = payload(response);
    return Array.isArray(list) ? list : [];
  }

  /**
   * Get permissions for a role
   */
  async getRolePermissions(roleId: string): Promise<Permission[]> {
    const response = await apiService.get<Permission[]>(`${this.baseUrl}/${roleId}/permissions`);
    const list = payload(response);
    return Array.isArray(list) ? list : [];
  }

  /**
   * Assign permissions to role
   */
  async assignPermissions(roleId: string, permissionIds: string[]): Promise<Role> {
    const response = await apiService.post<Role>(`${this.baseUrl}/${roleId}/permissions`, {
      permission_ids: permissionIds,
    });
    return payload(response);
  }

  /**
   * Get role statistics
   */
  async getRoleStatistics(roleId: string): Promise<RoleStatistics> {
    const response = await apiService.get<RoleStatistics>(`${this.baseUrl}/${roleId}/statistics`);
    return payload(response);
  }

  /**
   * Get audit logs for a role
   */
  async getRoleAuditLogs(roleId: string, limit?: number): Promise<AuditLog[]> {
    const params = limit ? `?limit=${limit}` : '';
    const response = await apiService.get<AuditLog[]>(`${this.baseUrl}/${roleId}/audit-logs${params}`);
    const list = payload(response);
    return Array.isArray(list) ? list : [];
  }

  /**
   * Get recent permission changes
   */
  async getRecentPermissionChanges(limit?: number): Promise<AuditLog[]> {
    const params = limit ? `?limit=${limit}` : '';
    const response = await apiService.get<AuditLog[]>(`${this.baseUrl}/audit/recent-changes${params}`);
    const list = payload(response);
    return Array.isArray(list) ? list : [];
  }
}

export const roleService = new RoleService();
export default roleService;
